/**
 * The calendar a vacancy's figures are read against.
 *
 * Three surfaces print the same facts about one vacancy — the employer's
 * dashboard on the site, the vacancy's cover in the panel, and the reminder
 * email the backend sends before it closes — and each of them has to agree on
 * two things that are easy to get subtly wrong: what *day* a timestamp falls
 * on, and whether a count covers the vacancy's whole life.
 *
 * Deliberately free of imports, so that all three can load it: the backend and
 * the panel as they load everything else in `config/`, the site through the
 * `@config/*` alias (tests/shared-config-imports.test.mjs holds it to that). A
 * copy per surface is how three screens come to disagree about the same date.
 *
 * ── Which day a timestamp is ─────────────────────────────────────────
 *
 * `closing_date` is a day somebody picked in a date picker, stored as an
 * instant: the company form sends 00:00 UTC, the panel sends local midnight —
 * 22:00 UTC the evening *before*, from Madrid in summer. Reading the UTC date
 * of the second one names the wrong day. Reading both in Madrid time names the
 * day that was picked, and also does for a date typed in Mexico (06:00 UTC,
 * still that day in Madrid). So every "which day" question here is asked in
 * `Europe/Madrid`, the same zone the team works in.
 *
 * ── Since when a count is true ───────────────────────────────────────
 *
 * Nothing counted views or apply clicks before {@link TRAFFIC_COUNTED_SINCE}.
 * A vacancy published before that day has figures for only part of its life,
 * and printing "12 vistas" beside it would claim more than the data holds —
 * PRODUCT.md, principle 3. Those figures carry "desde el 22 sep".
 */

/**
 * The day the public site started recording views and apply clicks.
 *
 * If the site deploy that ships the recording lands later than this, move this
 * date to that day — the label is a claim about when counting began, and it is
 * only true if it names the day it did.
 */
export const TRAFFIC_COUNTED_SINCE = "2026-09-22";

/** The zone every "which day is it" question is answered in. */
export const VACANCY_TIME_ZONE = "Europe/Madrid";

const DAY_MS = 86_400_000;

const MONTHS_LONG = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
];

const MONTHS_SHORT = [
    "ene", "feb", "mar", "abr", "may", "jun",
    "jul", "ago", "sep", "oct", "nov", "dic"
];

const WEEKDAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

/** A `YYYY-MM-DD` string, and nothing else, is a day. */
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

let dayFormatter: Intl.DateTimeFormat | null = null;

/**
 * The calendar day an instant falls on in Madrid, as `YYYY-MM-DD`.
 *
 * `null` for anything that does not parse, so a caller can print "sin fecha"
 * rather than "NaN de undefined". A value that is already a bare day is
 * returned as it is: a day has no time zone to convert.
 */
export function calendarDay(value: Date | string | null | undefined): string | null {
    if (!value) return null;
    if (typeof value === "string" && DAY_RE.test(value)) return value;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    // `en-CA` formats as YYYY-MM-DD, which is the only reason it is here.
    dayFormatter ??= new Intl.DateTimeFormat("en-CA", {
        timeZone: VACANCY_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    });
    return dayFormatter.format(date);
}

/** A day as the UTC midnight that starts it — for arithmetic, never for display. */
function dayStart(day: string): number {
    const match = DAY_RE.exec(day);
    if (!match) return Number.NaN;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Whole days from one day to another: 1 for tomorrow, -1 for yesterday. */
export function daysBetween(fromDay: string, toDay: string): number {
    return Math.round((dayStart(toDay) - dayStart(fromDay)) / DAY_MS);
}

/** `day` moved by `days` calendar days. */
export function addDays(day: string, days: number): string {
    return new Date(dayStart(day) + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The instant to store for a day somebody chose: 00:00 UTC.
 *
 * The company form's convention, and the one both readers agree on — Madrid
 * reads it as that same day, and so does every `getUTCDate()` on the site.
 * `retire-stale-jobs` adds a day before it compares, so the vacancy stays open
 * for the whole of the day named.
 */
export function dayAsStoredInstant(day: string): string {
    return `${day}T00:00:00.000Z`;
}

function parts(day: string): { year: number; month: number; date: number; weekday: number } | null {
    const start = dayStart(day);
    if (Number.isNaN(start)) return null;
    const d = new Date(start);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth(), date: d.getUTCDate(), weekday: d.getUTCDay() };
}

/** `21 sep` — for a dense row. The year only when it is not `referenceDay`'s. */
export function shortDayLabel(day: string, referenceDay?: string | null): string {
    const p = parts(day);
    if (!p) return "";
    const ref = referenceDay ? parts(referenceDay) : null;
    const year = ref && ref.year !== p.year ? ` ${p.year}` : "";
    return `${p.date} ${MONTHS_SHORT[p.month]}${year}`;
}

/** `21 de septiembre` — for a sentence. The year only when it is not `referenceDay`'s. */
export function longDayLabel(day: string, referenceDay?: string | null): string {
    const p = parts(day);
    if (!p) return "";
    const ref = referenceDay ? parts(referenceDay) : null;
    const year = ref && ref.year !== p.year ? ` de ${p.year}` : "";
    return `${p.date} de ${MONTHS_LONG[p.month]}${year}`;
}

/** `domingo 28 de septiembre`. */
export function weekdayDayLabel(day: string, referenceDay?: string | null): string {
    const p = parts(day);
    if (!p) return "";
    return `${WEEKDAYS[p.weekday]} ${longDayLabel(day, referenceDay)}`;
}

/**
 * Whether the counts cover this vacancy's whole time on the board.
 *
 * True only when we know it went live on or after the day counting started. An
 * unknown publication date is treated as "before": the qualified label is
 * always true, the unqualified one only sometimes.
 */
export function trafficCoversWholeLife(publishedAt: Date | string | null | undefined): boolean {
    const day = calendarDay(publishedAt);
    return day !== null && daysBetween(TRAFFIC_COUNTED_SINCE, day) >= 0;
}

/**
 * `"desde el 21 sep"` when the figures cover only part of the vacancy's life,
 * `null` when they cover all of it and need no qualifier.
 */
export function trafficSinceLabel(publishedAt: Date | string | null | undefined): string | null {
    if (trafficCoversWholeLife(publishedAt)) return null;
    return `desde el ${shortDayLabel(TRAFFIC_COUNTED_SINCE)}`;
}

/**
 * Days a vacancy has been live, counted in calendar days: 0 on the day it was
 * published. `null` when the publication date is unknown.
 */
export function daysLive(
    publishedAt: Date | string | null | undefined,
    now: Date = new Date()
): number | null {
    const from = calendarDay(publishedAt);
    const today = calendarDay(now);
    if (!from || !today) return null;
    return Math.max(0, daysBetween(from, today));
}

/**
 * Calendar days until the closing day, 0 on the day itself and negative once it
 * has passed. `null` when there is no closing date.
 */
export function daysUntilClosing(
    closingDate: Date | string | null | undefined,
    now: Date = new Date()
): number | null {
    const closing = calendarDay(closingDate);
    const today = calendarDay(now);
    if (!closing || !today) return null;
    return daysBetween(today, closing);
}

/** "hoy", "mañana", "en 5 días", "hace 2 días" — relative to today. */
export function relativeDays(days: number): string {
    if (days === 0) return "hoy";
    if (days === 1) return "mañana";
    if (days === -1) return "ayer";
    return days > 0 ? `en ${days} días` : `hace ${-days} días`;
}

/** "1 vista", "12 vistas" — Spanish number agreement for a count and a noun. */
export function countLabel(count: number, singular: string, plural: string): string {
    return `${count} ${count === 1 ? singular : plural}`;
}
